import React from "react";
import { motion } from "framer-motion";
import "./Testimonials.css";

/* ─── testimonial data ─── */
const testimonials = [
  {
    name: "Michael Thompson",
    role: "CEO, NorthPeak Solutions",
    text: "Quantech transformed our online presence completely. Their attention to detail and creative solutions exceeded all our expectations. The website they built drives 3× more leads than before.",
    stars: 5,
    accent: "#6366f1",
    accentAlpha: "rgba(99, 102, 241, 0.08)",
    gradient: "linear-gradient(135deg, #6366f1, #818cf8)",
  },
  {
    name: "Emily Carter",
    role: "Founder, GreenLeaf Organics",
    text: "Working with Quantech was a game-changer for our business. They understood our vision perfectly and delivered a stunning e-commerce site that our customers absolutely love browsing.",
    stars: 5,
    accent: "#ec4899",
    accentAlpha: "rgba(236, 72, 153, 0.08)",
    gradient: "linear-gradient(135deg, #ec4899, #f472b6)",
  },
  {
    name: "Daniel Brooks",
    role: "Director, BuildRight Infra",
    text: "Professional, responsive, and incredibly talented. Quantech delivered our real-estate platform ahead of schedule and the quality was outstanding. Would highly recommend to anyone.",
    stars: 5,
    accent: "#f59e0b",
    accentAlpha: "rgba(245, 158, 11, 0.08)",
    gradient: "linear-gradient(135deg, #f59e0b, #fbbf24)",
  },
  {
    name: "Sophie Nguyen",
    role: "Marketing Head, FitPulse",
    text: "The team at Quantech brought our fitness brand to life online. The animations, lightning-fast speed, and premium design are exactly what we needed to dominate our market.",
    stars: 5,
    accent: "#10b981",
    accentAlpha: "rgba(16, 185, 129, 0.08)",
    gradient: "linear-gradient(135deg, #10b981, #34d399)",
  },
  {
    name: "Ryan Patel",
    role: "CTO, CloudNine Tech",
    text: "Exceptional development skills and consistent communication throughout the project. Quantech's technical expertise in React and modern web is truly world-class.",
    stars: 5,
    accent: "#3b82f6",
    accentAlpha: "rgba(59, 130, 246, 0.08)",
    gradient: "linear-gradient(135deg, #3b82f6, #60a5fa)",
  },
  {
    name: "Olivia Martin",
    role: "Owner, Artisan Bakery Co.",
    text: "From concept to launch, Quantech made the entire process seamless. Our bakery website looks absolutely beautiful and has significantly increased our online orders and reputation.",
    stars: 5,
    accent: "#f97316",
    accentAlpha: "rgba(249, 115, 22, 0.08)",
    gradient: "linear-gradient(135deg, #f97316, #fb923c)",
  },
  {
    name: "Ethan Wallace",
    role: "Founder, EduSpark",
    text: "Quantech delivered a robust ed-tech platform handling thousands of concurrent users effortlessly. Their performance optimization and scalable architecture is remarkable.",
    stars: 5,
    accent: "#8b5cf6",
    accentAlpha: "rgba(139, 92, 246, 0.08)",
    gradient: "linear-gradient(135deg, #8b5cf6, #a78bfa)",
  },
  {
    name: "Chloe Bennett",
    role: "Manager, UrbanStay Hotels",
    text: "Our hotel booking website by Quantech has been a massive success. The UI is intuitive, fully mobile-optimized, and our booking conversion rate jumped 40% after launch.",
    stars: 5,
    accent: "#14b8a6",
    accentAlpha: "rgba(20, 184, 166, 0.08)",
    gradient: "linear-gradient(135deg, #14b8a6, #2dd4bf)",
  },
];

const row1 = testimonials.slice(0, 4);
const row2 = testimonials.slice(4);

/* ─── star SVG ─── */
const Star = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

/* ─── single card ─── */
const TestimonialCard = ({ item }) => {
  const initial = item.name.charAt(0);

  return (
    <div
      className="testimonial-card"
      style={{
        "--card-accent": item.accent,
        "--card-accent-alpha": item.accentAlpha,
      }}
    >
      <div className="testimonial-stars">
        {Array.from({ length: item.stars }, (_, i) => (
          <Star key={i} />
        ))}
      </div>

      <p className="testimonial-quote">
        <span className="quote-icon">&ldquo;</span>
        {item.text}
      </p>

      <div className="testimonial-author">
        <div className="testimonial-avatar" style={{ background: item.gradient }}>
          {initial}
        </div>
        <div className="testimonial-author-info">
          <span className="testimonial-name">{item.name}</span>
          <span className="testimonial-role">{item.role}</span>
        </div>
      </div>
    </div>
  );
};

/* ─── marquee row ─── */
const MarqueeRow = ({ items, direction = "left" }) => (
  <div className="marquee-wrapper">
    <div className={`marquee-track marquee-track--${direction}`}>
      {[...items, ...items].map((item, i) => (
        <TestimonialCard key={i} item={item} />
      ))}
    </div>
  </div>
);

/* ─── main component ─── */
const Testimonials = () => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: "easeOut" }}
    viewport={{ once: true }}
    className="testimonials-section"
    id="testimonials"
  >
    {/* dot grid background */}
    <div className="testimonials-grid-bg" />

    {/* header */}
    <div className="testimonials-header">
      <motion.div
        className="testimonials-badge"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        viewport={{ once: true }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
        Client Love
      </motion.div>

      <h2 className="testimonials-title">
        What Our <span className="highlight">Clients</span> Say
      </h2>

      <p className="testimonials-subtitle">
        Real stories from businesses we've helped grow with stunning digital experiences.
      </p>

      {/* Stats */}
      <div className="testimonials-stats">
        <div className="stat-item">
          <div className="stat-value">50+</div>
          <div className="stat-label">Happy Clients</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">4.9★</div>
          <div className="stat-label">Avg Rating</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">100%</div>
          <div className="stat-label">Satisfaction</div>
        </div>
      </div>
    </div>

    {/* two counter-scrolling rows */}
    <MarqueeRow items={row1} direction="left" />
    <MarqueeRow items={row2} direction="right" />
  </motion.div>
);

export default Testimonials;
